import React, { useState } from 'react';
import { Button, Icon, Input, Modal, Pagination, Select, Toast, Tooltip } from '@roo/roo';
import TableNew, { Column as TableNewColum } from 'RooV1.12/TableNew';
import Table, { Column } from '@roo/roo/Table';
import { getCookie } from '@utiljs/cookie';
import dayJs from 'dayjs';
import { Confirm as confirm } from '@roo/roo/Modal';
import { RangePicker } from 'RooV1.12/DatePicker';
import request from '@/utils/request';
import Util from './util';
import {
    CONTRACT_TYPE,
    DistributeSource,
    HAS_EXPIRE,
    ICooperateList,
    postSign,
    SIGN_STATUS,
    SignItem,
    SignState,
    SOURCE_TYPE,
} from './api';
import SignModal from './components/SignModal';
import ContractModal from './components/ContractModal';
import JumpOrderBtn from './components/JumpOrderBtn';
import SignBatchProgressModal from './components/SignBatchProgressModal';
import SelectGroup, { CustomRef } from '@/components/SelectorGroup';
import DistributeRecord from './components/DistributeRecord';
import Utils from '@/utils/utils';
import './list.scss';

type RangeValue = Array<string | number | null> | null;

interface IState {
    signStateList: SignState[]; // 0-未签约，1-签约中，2-已签约，3-合作到期，4-合作终止，5-签约失败
    total: number;
    distributeList: SignItem[];
    statusSelected: number;
    sourceSelected: number;
    distributeStateSelected: number;
    distributeTime?: number[];
    startTime?: number[];
    poiSignTime?: number[];
    loading: boolean;
    showModal: number;
    activeItem: SignItem;
    reason: string;
    queryWmPoiId: string;
    exportLoading: boolean;
    wmPoiIdState?: 'error' | 'success';
    privateNumber: string;
    managerMisId: string;
    contactLoading: boolean;
    defaultSelected: string[];
    signData: any;
    batchOperation: number;
    coopSelected: number;

    // 批量签约的合作模式
    batchCoopMode: number;
    cooperateList: ICooperateList;

    // 批量签约弹出签约弹窗的activeItem，默认是签约列表的第一个
    batchSignDemoItem: SignItem;

    tempDates?: RangeValue;
}

interface Operation {
    value: number;
    label: string;
    fn?: (item: SignItem) => void;
    render?: (value: number, row: SignItem) => React.ReactNode;
}

interface IProps {}

const PAGE_SIZE = 50;

/**
 * 签约状态与签约操作对应关系
 * key对应 0-待签约，1-签约中，2-已签约，3-合作到期，4-合作终止，5-签约失败
 * value: 1-签约 2-补充未签约原因 4-撤回签约 8-查看合同
 */
const operationMap: { [item in SIGN_STATUS]: number } = {
    0: 3,
    1: 4,
    2: 24,
    3: 1,
    4: 0,
    5: 1,
};

const signNameMap: { [item in SIGN_STATUS]: string } = {
    0: '待签约',
    1: '签约中',
    2: '已签约',
    3: '合作到期',
    4: '合作中止',
    5: '签约失败',
};
const sourceNameMap: { [item in SOURCE_TYPE]: string } = {
    0: '全部',
    1: '官方代运营业务',
    2: '服务市场业务',
    3: '品牌分账',
    4: 'C+代运营业务',
};
const distributeNameMap = {
    '-1': '全部类型',
    0: '手动分配',
    1: '新店模式',
};

const signSelectList = [
    SIGN_STATUS.NOT_SIGNED,
    SIGN_STATUS.SIGNING,
    SIGN_STATUS.SIGNED,
    SIGN_STATUS.EXPIRED,
    SIGN_STATUS.END,
    SIGN_STATUS.FAILED_SIGN,
];

const sourceSelectList = [
    SOURCE_TYPE.ALL,
    SOURCE_TYPE.OFFICIAL,
    SOURCE_TYPE.SELF,
    SOURCE_TYPE.SERVICE,
    SOURCE_TYPE.C_PLUS,
];

const distributeSourceList = [DistributeSource.ALL, DistributeSource.OLD, DistributeSource.NEWPOI];

class List extends React.Component<IProps, IState> {
    // 表格格式
    columns: TableNewColum<SignItem>[] = [
        // {
        //     renderTh: () => (
        //         <h4 key="selectAll">
        //             <p>当前页全选</p>
        //             <SelectAll onChange={this.handleSelectAll} />
        //         </h4>
        //     ),
        //     prop: 'id',
        //     label: '选择',
        //     render: (id: string, row, index) => <SelectItem rowKey={index} />,
        //     // width: 120,
        //     // fixed: 'left',
        // },
        {
            dataIndex: 'customerSource',
            title: '客户来源',
            width: 110,
            render: (text: SOURCE_TYPE, record) =>
                `${sourceNameMap[text]}${record.distributeSource === 1 ? '[新店模式]' : ''}`,
        },
        {
            dataIndex: 'contractName',
            title: '合作模式',
            width: 100,
        },
        {
            dataIndex: 'status',
            title: '签约状态',
            width: 100,
            render: (text: SIGN_STATUS) => signNameMap[text],
        },
        {
            dataIndex: 'wmPoiId',
            title: '商家ID',
            width: 80,
        },
        {
            dataIndex: 'cityName',
            title: '商家城市',
            width: 100,
        },
        {
            dataIndex: 'simpleProviderName',
            title: '服务商简称',
            width: 100,
        },
        {
            dataIndex: 'poiName',
            title: '门店名称',
            width: 100,
        },
        {
            dataIndex: 'phone',
            title: '联系电话',
            width: 120,
        },
        {
            dataIndex: 'linkman',
            title: '联系人',
            width: 80,
        },
        {
            dataIndex: 'poiSettlementRuleDescList',
            title: '商家结算规则',
            width: 300,
            render: (text: string[]) => (text ? <ParagraphOpenHide content={text} /> : null),
        },
        {
            dataIndex: 'providerSettlementRuleDescList',
            title: '服务商结算规则',
            width: 300,
            render: (text: string[]) => (text ? <ParagraphOpenHide content={text} /> : null),
        },
        {
            dataIndex: 'poiAddress',
            title: '商家地址',
            width: 150,
        },
        {
            dataIndex: 'orderId',
            title: '订单ID',
            width: 120,
        },
        {
            dataIndex: 'poiSignTime',
            title: '签约时间',
            render: timestamp => {
                if (timestamp === 0) return '';
                return dayJs(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
            },
            width: 120,
        },
        {
            dataIndex: 'startTime',
            title: '服务开始时间',
            width: 120,
            render: timestamp => {
                if (timestamp === 0) return '';
                return dayJs(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
            },
        },
        {
            dataIndex: 'endTime',
            title: '服务结束时间',
            width: 120,
            render: timestamp => {
                if (timestamp === 0) return '';
                return dayJs(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
            },
        },
        {
            dataIndex: 'cancelTime',
            title: '解约时间',
            width: 120,
            render: timestamp => {
                if (timestamp === 0) return '';
                return dayJs(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
            },
        },
        {
            dataIndex: 'cancelTypeDesc',
            title: '解约方式',
            width: 120,
        },
        {
            dataIndex: 'distributeTime',
            title: '分配时间',
            render: (text: any) => dayJs(text * 1000).format('YYYY-MM-DD'),
            width: 120,
        },
        {
            dataIndex: 'operation',
            title: '操作',
            width: 150,
            fixed: 'right',
            render: (text: any, row: SignItem) => {
                const { contactLoading } = this.state;
                return (
                    <div>
                        {this.renderOperation(row)}
                        <Button
                            type="text"
                            onClick={() => {
                                this.setState({
                                    activeItem: row,
                                    showModal: 8, // 新增弹窗标识
                                });
                            }}
                        >
                            查看操作记录
                        </Button>
                        {
                            // 仅针对客户来源=官方代运营业务的门店
                            row.customerSource === 1 && (
                                <>
                                    <Button
                                        disabled={contactLoading}
                                        type="text"
                                        onClick={() => {
                                            this.showContactModal(row);
                                        }}
                                    >
                                        联系负责人
                                    </Button>
                                    <Button
                                        disabled={contactLoading}
                                        type="text"
                                        onClick={() => {
                                            this.showContactManagerModal(row);
                                        }}
                                    >
                                        联系业务经理
                                    </Button>
                                </>
                            )
                        }
                    </div>
                );
            },
        },
    ];

    modalColumns = this.columns
        .slice(1, 8)
        .concat(this.columns.slice(10, 11))
        .map(item => ({
            ...item,
            prop: item.dataIndex,
            label: item.title,
        })) as Column<SignItem>[];

    /**
     * 是否是批量操作 默认操作单个
     * 批量操作和单个操作签约信息填写弹窗，通过此值判断点击确定后是批量签约还是单个签约
     */
    isBatchOperation: boolean = false;

    selectorHandle: CustomRef = {};

    signStateList: SignState[] = [
        {
            value: -1,
            label: '全部',
        },
        ...signSelectList.map(val => ({ value: val, label: signNameMap[val] })),
    ];

    sourceStateList: SignState[] = sourceSelectList.map(val => ({
        value: val,
        label: sourceNameMap[val],
    }));

    allSelected: number[] = [];

    distributeStateList: SignState[] = distributeSourceList.map(val => ({
        value: val,
        label: distributeNameMap[val],
    }));

    // 操作1：签约
    toSignContract = (item: SignItem) => {
        this.isBatchOperation = false;
        this.setState({
            activeItem: item,
            showModal: 1,
        });
    };

    // 操作2： 补充未签约原因
    toAddReason = (item: SignItem) => {
        this.setState({
            activeItem: item,
            showModal: 4,
        });
    };

    // 操作3： 撤回签约
    toWithdraw = (item: SignItem) => {
        this.setState({
            activeItem: item,
            showModal: 3,
        });
    };

    // 操作4： 查看合同
    toSeeContract = (item: SignItem) => {
        this.setState({
            activeItem: item,
            showModal: 2,
        });
    };

    // 获取url中的参数
    getUrlSearchParms = (key: string, defaultValue: string) => {
        const obj = Utils.urlParse();
        if (Object.keys(obj).includes(key)) {
            return obj[key];
        }
        return defaultValue;
    };

    providerId = +getCookie('serviceProviderID');

    lastPageNum: number = 1;

    // 操作列表
    operationList: Operation[] = [
        {
            value: 1,
            label: '签约',
            fn: this.toSignContract,
            render: (value, row) => {
                // 8天倒计时，注意28800是东八区
                const distributeSourceToNowDays =
                    8 -
                    (Math.floor(+new Date() / 86400000) -
                        Math.floor(((row.distributeTime as number) + 28800) / 86400));
                if (
                    row.distributeSource === 1 &&
                    distributeSourceToNowDays >= 0 &&
                    distributeSourceToNowDays <= 8
                ) {
                    return (
                        <Tooltip
                            effect="light"
                            placement="topLeft"
                            content={`${distributeSourceToNowDays}天超期未处理，将不可签约新店模式`}
                            visible
                            manual
                        >
                            <Button
                                type="text"
                                key={value}
                                onClick={() => this.toSignContract(row)}
                            >
                                签约
                            </Button>
                        </Tooltip>
                    );
                }
                return (
                    <Button type="text" key={value} onClick={() => this.toSignContract(row)}>
                        签约
                    </Button>
                );
            },
        },
        {
            value: 2,
            label: '补充未签约原因',
            fn: this.toAddReason,
        },
        {
            value: 4,
            label: '撤回签约',
            fn: this.toWithdraw,
        },
        {
            value: 8,
            label: '查看合同',
            fn: this.toSeeContract,
        },
        {
            value: 16,
            label: '查看订单',
            render: (value, row) => <JumpOrderBtn key="orderId" distributeId={row.id} />,
        },
    ];

    constructor(props: IProps) {
        super(props);
        this.state = {
            signStateList: [
                {
                    value: -1,
                    label: '全部',
                },
            ],
            total: 0,
            statusSelected: -1,
            sourceSelected: 0,
            distributeStateSelected: -1,
            distributeList: [],
            loading: false,
            showModal: 0,
            activeItem: {
                id: '0',
                distributeTime: 0,
                wmPoiId: 0,
                poiName: '',
                phone: '',
                linkman: '',
                cityName: '',
                providerId: 0,
                simpleProviderName: '',
                status: SIGN_STATUS.NOT_SIGNED,
                noPartnerReason: '',
                contractName: '',
                settleRule: '',
                startTime: 0,
                endTime: 0,
                hasExpire: HAS_EXPIRE.notExpire, // 已废弃， SIGN_STATUS.EXPIRED 重复
                contractList: [],
                customerSource: 1,
                distributeSource: 0,
                coopMode: CONTRACT_TYPE.All,
                poiSettlementRuleDescList: [],
                providerSettlementRuleDescList: [],
            },
            batchSignDemoItem: {
                id: '0',
                distributeTime: 0,
                wmPoiId: 0,
                poiName: '',
                phone: '',
                linkman: '',
                cityName: '',
                providerId: 0,
                simpleProviderName: '',
                status: SIGN_STATUS.NOT_SIGNED,
                noPartnerReason: '',
                contractName: '',
                settleRule: '',
                startTime: 0,
                endTime: 0,
                hasExpire: HAS_EXPIRE.notExpire, // 已废弃， SIGN_STATUS.EXPIRED 重复
                contractList: [],
                customerSource: 1,
                distributeSource: 0,
                coopMode: CONTRACT_TYPE.All,
                poiSettlementRuleDescList: [],
                providerSettlementRuleDescList: [],
            },
            reason: '',
            queryWmPoiId: '',
            exportLoading: false,
            wmPoiIdState: undefined,
            privateNumber: '',
            managerMisId: '',
            contactLoading: false,
            defaultSelected: [],
            signData: {},
            batchOperation: 1,
            coopSelected: 0,
            batchCoopMode: 0,
            cooperateList: [],
        };
    }

    async componentDidMount() {
        // 通过url获取合作模式信息
        const coop = this.getUrlSearchParms('coopSelected', '0') as string;
        const data = await this.getAllCoopmode();
        this.setState(
            {
                coopSelected: parseInt(coop, 10),
                cooperateList: data,
            },
            () => {
                this.init();
            },
        );
    }

    getAllCoopmode = async (customSource = -1) => {
        try {
            const { data } = await request.get(
                '/api/thirdpartnar/coopmode/list',
                { customSource },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            if (data.length > 0) {
                data.unshift({
                    value: 0,
                    label: '全部',
                });
            }
            return data;
        } catch (e) {
            console.log('error: ', e);
            return [];
        }
    };

    /**
     * 获取用1248等比数列的和展示操作组合。
     * 比如5 即1+4 表示1对应的操作和4对应的操作的组合
     * @param item SignItem
     * @returns number
     */
    getOperationNumber = (item: SignItem): number => {
        const { status } = item;
        let operationNumber = operationMap[status];
        // 已过期订单，且操作里未包含签约则增加签约
        if (status === SIGN_STATUS.EXPIRED || item.hasExpire) {
            operationNumber = 1;
        }

        /** 来源为非官方代运营则不需要填写未签约原因 */
        // eslint-disable-next-line no-bitwise
        if (item.customerSource !== 1 && operationNumber & 2) {
            operationNumber -= 2;
        }
        return operationNumber;
    };

    // 展示操作
    renderOperation = (item: SignItem): React.ReactNode[] => {
        const operationNumber = this.getOperationNumber(item);
        // eslint-disable-next-line no-bitwise
        const useList = this.operationList.filter(v => v.value & operationNumber);
        return useList.map(({ value, label, fn, render }) => {
            if (render) {
                return render(value, item);
            }
            return (
                <Button type="text" key={value} onClick={() => fn && fn(item)}>
                    {label}
                </Button>
            );
        });
    };

    handleWithdraw = async () => {
        await request.post('/api/thirdpartnar/sign/withdraw', {
            providerId: this.providerId,
            distributeId: this.state.activeItem.id,
        });
        this.getSignList();
        this.setState({
            loading: false,
            showModal: 0,
        });
    };

    handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { distributeList } = this.state;
        let defaultSelected: any[] = [];
        if (e.target.checked) {
            defaultSelected = distributeList.map((row, index) => index);
        }
        this.setState({
            defaultSelected,
        });
    };

    showContactModal = async (item: SignItem) => {
        const parmas = {
            providerId: this.providerId,
            distributeId: item.id,
        };
        this.setState({
            contactLoading: true,
        });
        try {
            const res = await request.get('/api/thirdpartnar/sign/getKpPrivateNumber', parmas);
            if (!res.data) return;
            this.setState({
                privateNumber: res.data,
                showModal: 5,
            });
        } finally {
            this.setState({
                contactLoading: false,
            });
        }
    };

    showContactManagerModal = async (item: SignItem) => {
        const params = {
            providerId: this.providerId,
            distributeId: item.id,
        };
        this.setState({
            contactLoading: true,
        });
        try {
            const res = await request.get('/api/thirdpartnar/sign/getBDContact', params);
            if (!res.data) return;
            this.setState({
                managerMisId: res.data,
                showModal: 6,
            });
        } finally {
            this.setState({
                contactLoading: false,
            });
        }
    };

    handleAddReason = async () => {
        await request.post('/api/thirdpartnar/sign/record/reason', {
            providerId: this.providerId,
            distributeId: this.state.activeItem.id,
            reason: this.state.reason,
        });
        this.getSignList();
        this.setState({
            reason: '',
            showModal: 0,
        });
    };

    handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            reason: e.target.value,
        });
    };

    init() {
        // 获取列表数据
        this.getSignList();
    }

    /**
     * 获取列表
     * @param status 筛选项
     * @param pageNum 页数
     * @param pageSize
     */
    getSignList = async (pageNum = this.lastPageNum, pageSize = PAGE_SIZE) => {
        const {
            statusSelected: status = -1,
            sourceSelected,
            queryWmPoiId,
            poiSignTime,
            distributeTime,
            startTime,
            distributeStateSelected,
            coopSelected,
        } = this.state;
        if (!this.ifQueryParamsChecked()) {
            this.setState({
                loading: false,
            });
            return;
        }
        let res;
        try {
            res = await request.post(
                '/api/thirdpartnar/sign/list',
                {
                    providerId: this.providerId,
                    distributeSource: distributeStateSelected,
                    status,
                    customerSource: sourceSelected,
                    wmPoiIds: queryWmPoiId.replace('，', ','),
                    poiSignTimeLeft: poiSignTime?.[0] || 0,
                    poiSignTimeRight: poiSignTime?.[1] || 0,
                    distributeTimeLeft: distributeTime?.[0] || 0,
                    distributeTimeRight: distributeTime?.[1] || 0,
                    startTimeLeft: startTime?.[0] || 0,
                    startTimeRight: startTime?.[1] || 0,
                    pageNum,
                    pageSize,
                    coopMode: coopSelected,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            const {
                data: { total, distributeList } = {},
            }: { data: { total?: number; distributeList?: any } } = res;
            this.setState({
                total: total || 0,
                distributeList,
                defaultSelected: [],
            });
            this.allSelected = distributeList.map((item, i) => i);
            // eslint-disable-next-line no-empty
        } catch (e) {}
        this.setState({
            loading: false,
        });
    };

    handleSignFormOk = (data: any) => {
        if (!this.isBatchOperation) {
            return this.queryToSign(data);
        }
        return this.toSignBatch(data);
    };

    toSignBatch = (data: any) => {
        this.setState({
            signData: data,
            showModal: 7,
            batchOperation: 1,
        });
        return Promise.resolve();
    };

    /**
     * 签约请求（单个）
     * @param data
     */
    queryToSign = async (data: any) => {
        const { activeItem } = this.state;
        const res = await postSign({
            providerId: this.providerId,
            distributeId: activeItem.id,
            data: {
                ...data,
                signType: Util.signTypeGenerate(activeItem.coopMode, activeItem.customerSource),
            },
        });
        if (res && res.code === 0) {
            confirm({
                title: '您已签约，合同已流转至商家。签约有效期仅保留3天，请联系商家尽快完成签约。',
                status: 'success',
            });
        }
        this.getSignList();
        this.setState({
            showModal: 0,
        });
        return res;
    };

    // 签约类型筛选
    handleChangeStatusSelected = (value: number) => {
        this.setState({
            statusSelected: value,
        });
    };

    // 客户来源筛选
    handleChangeSourceSelected = async (value: number) => {
        const data = await this.getAllCoopmode(value);
        this.setState({
            sourceSelected: value,
            cooperateList: data,
            coopSelected: 0,
        });
    };

    // 合作模式筛选
    handleChangeCoopSelected = (value: number) => {
        this.setState({
            coopSelected: value,
        });
    };

    // 分配类型
    handleChangeDistributeStateSelected = (value: number) => {
        this.setState({
            distributeStateSelected: value,
        });
    };

    // 门店Id筛选
    handleWmPoiIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            queryWmPoiId: e.target.value,
        });
    };

    handleClearWmPoiId = () => {
        this.setState({
            queryWmPoiId: '',
        });
    };

    // 分配时间筛选
    handleDistributeTimeChange = (dates: number[]) => {
        this.setState({
            distributeTime: this.formateDates(dates),
        });
    };

    // 服务开始时间筛选
    handleStartTimeChange = (dates: number[]) => {
        this.setState({
            startTime: this.formateDates(dates),
        });
    };

    // 签约时间筛选
    handlePoiSignTimeChange = (dates: number[]) => {
        this.setState({
            poiSignTime: this.formateDates(dates),
        });
    };

    formateDates = (dates: number[]) => {
        const [startNum, endNum] = dates;
        // 将起始日期设置为当天的第一秒
        const startDayJs = new Date(startNum * 1000);
        startDayJs.setHours(0, 0, 0, 0);
        const startDayTimestamp = Math.floor(startDayJs.getTime() / 1000); // 转换为秒级时间戳

        // 将截止日期设置为当天的最后一秒
        const endDayJs = new Date(endNum * 1000);
        endDayJs.setHours(23, 59, 59, 999);
        const endDayTimestamp = Math.floor(endDayJs.getTime() / 1000); // 转换为秒级时间戳

        return [startDayTimestamp, endDayTimestamp];
    };

    // 查询按钮
    handleQuery = () => {
        const { coopSelected } = this.state;
        this.setState({
            loading: true,
            batchCoopMode: coopSelected,
        });
        this.getSignList();
    };

    // 导出
    handleExport = async () => {
        const {
            statusSelected,
            sourceSelected,
            queryWmPoiId,
            coopSelected,
            distributeTime,
            poiSignTime,
            startTime,
        } = this.state;
        if (!this.ifQueryParamsChecked()) {
            return;
        }
        this.setState({
            exportLoading: true,
        });
        const params: any = {
            providerId: this.providerId,
            status: statusSelected,
            customerSource: sourceSelected,
            poiSignTimeLeft: poiSignTime?.[0] || 0,
            poiSignTimeRight: poiSignTime?.[1] || 0,
            distributeTimeLeft: distributeTime?.[0] || 0,
            distributeTimeRight: distributeTime?.[1] || 0,
            startTimeLeft: startTime?.[0] || 0,
            startTimeRight: startTime?.[1] || 0,
            coopMode: coopSelected,
        };
        if (queryWmPoiId) {
            params.wmPoiIds = queryWmPoiId.replace('，', ',');
        }

        try {
            const res: any = await request.post('/api/thirdpartnar/sign/export', params, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            window.open(res.data);
        } catch (e) {
            console.log('error: ', e);
        }

        this.setState({
            exportLoading: false,
        });
    };

    // 请求列表和导出接口参数校验
    ifQueryParamsChecked = () => {
        const { queryWmPoiId } = this.state;
        if (queryWmPoiId !== '' && !/^[1-9][0-9,，]*$/.test(queryWmPoiId)) {
            Toast.open({
                title: '门店id错误',
                position: 'topCenter',
                children: '多个门店id请按 id,id,... 的格式填写',
                closable: true,
                duration: 5000,
                status: 'fail',
            });
            this.setState({
                wmPoiIdState: 'error',
            });
            return false;
        }
        this.setState({
            wmPoiIdState: undefined,
        });
        return true;
    };

    // 翻页
    handlePageChange = (page: number) => {
        this.lastPageNum = page;
        this.getSignList(page);
    };

    getAllSelect = () => {
        if (!this.selectorHandle.getSelected) {
            Toast.open({
                title: '发生了错误，请刷新重试',
            });
            return [];
        }
        return this.selectorHandle.getSelected();
    };

    // 批量签约 签约选中项
    signByBatch = () => {
        this.batchCheck(1)
            .then((arr: SignItem[]) => {
                this.isBatchOperation = true;
                this.setState({
                    showModal: 1,
                    batchSignDemoItem: arr[0],
                });
            })
            .catch(e => {
                console.log('e', e);
            });
    };

    // 批量撤回 撤回选中项
    withdrawByBatch = () => {
        this.batchCheck(4).then(() => {
            this.isBatchOperation = true;
            this.setState({
                batchOperation: 4,
                showModal: 7,
            });
        });
    };

    batchCheck = (operationNumber: number): Promise<SignItem[]> => {
        const { distributeList } = this.state;
        const allSeleted = this.getAllSelect();
        if (allSeleted.length === 0) {
            Modal.confirm({
                status: 'danger',
                title: '请先选择需要操作的条目',
                size: 'lg',
            });
            return Promise.reject(new Error('未选择条目'));
        }
        let operationName = '';
        const fIdx = this.operationList.findIndex(item => item.value === operationNumber);
        if (fIdx === -1) {
            return Promise.reject(
                new Error(`批量操作函数参数operationNumber: ${operationNumber}不存在`),
            );
        }
        operationName = this.operationList[fIdx].label as string;
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line max-len
            const checkRes = allSeleted.filter(
                index => !this.hasOperationCheck(distributeList[index], operationNumber),
            );
            //校验客户来源是否一致，不一致不能进行批量签约操作
            const firstCustomSource = distributeList[allSeleted[0]].customerSource;
            const isCustomSourceDifferent = allSeleted.filter(
                index => distributeList[index].customerSource !== firstCustomSource,
            );
            if (checkRes.length > 0) {
                const itemList = checkRes.map(idx => distributeList[idx]);
                Modal.confirm({
                    status: 'danger',
                    title: `以下条目状态不可${operationName}`,
                    size: 'lg',
                    className: 'signbatch-modal',
                    children: <Table rowKey="id" columns={this.modalColumns} data={itemList} />,
                });
                reject(new Error('校验失败'));
            } else {
                const itemList = allSeleted.map(index => distributeList[index]);
                const success = isCustomSourceDifferent.length === 0;
                Modal.confirm({
                    status: success ? 'info' : 'danger',
                    title: success ? (
                        `确定要对以下条目进行"${operationName}"操作吗？`
                    ) : (
                        <div style={{ color: '#F76C6C' }}>
                            所选信息客户来源不同，无法批量签约。请单独发起签约操作
                        </div>
                    ),
                    size: 'lg',
                    confirmText: success ? '确定' : '我知道了',
                    className: 'signbatch-modal',
                    onConfirm: success ? () => resolve(itemList) : () => {},
                    children: <Table rowKey="id" columns={this.modalColumns} data={itemList} />,
                });
            }
        });
    };

    /**
     * 检查该项是否可以进行某种操作
     * @param item SignItem
     * @param operationValue 见 operationMap 注释中的操作值
     */
    hasOperationCheck = (item: SignItem, operationValue: number) => {
        const operationNumber = this.getOperationNumber(item);
        // eslint-disable-next-line no-bitwise
        return !!(operationNumber & operationValue);
    };

    closeModals = (noReload = true) => {
        this.setState({
            showModal: 0,
        });
        if (!noReload) {
            this.getSignList();
        }
    };

    disabled31DaysDate = (cur: dayJs.Dayjs) => {
        const { tempDates } = this.state;
        if (!tempDates || !tempDates[0]) {
            return false;
        }
        // 转换为dayJs，并将毫秒数清0排除干扰(cur和start会相差几毫秒)
        const startDateDayJs = dayJs(Number(tempDates[0]) * 1000).millisecond(0);
        cur = cur.millisecond(0);

        const tooLate = cur.diff(startDateDayJs, 'day') > 30;
        const tooEarly = startDateDayJs.diff(cur, 'day') > 30;
        return !!tooLate || !!tooEarly;
    };

    onCalendarChange = time => {
        this.setState({ tempDates: time });
    };

    onRangePickerOpenChange = open => {
        this.setState({ tempDates: open ? [null, null] : null });
    };

    render() {
        const {
            state,
            columns,
            signStateList,
            sourceStateList,
            distributeStateList,
            closeModals,
            handleSignFormOk,
        } = this;
        const {
            loading,
            exportLoading,
            distributeList,
            total,
            showModal,
            activeItem,
            statusSelected,
            sourceSelected,
            distributeStateSelected,
            queryWmPoiId,
            privateNumber,
            managerMisId,
            coopSelected,
            batchCoopMode,
            cooperateList,
            batchSignDemoItem,
            distributeTime,
            startTime,
            poiSignTime,
        } = state;

        return (
            <div className="page client-list">
                <div className="client-top">
                    <div className="filter-l">
                        <p>签约状态</p>
                        <Select
                            options={signStateList}
                            value={statusSelected}
                            onChange={this.handleChangeStatusSelected}
                        />
                    </div>
                    <div className="filter-l">
                        <p>客户来源</p>
                        <Select
                            options={sourceStateList}
                            value={sourceSelected}
                            onChange={this.handleChangeSourceSelected}
                        />
                    </div>
                    <div className="filter-l">
                        <p>合作模式</p>
                        <Select
                            options={cooperateList}
                            value={coopSelected}
                            onChange={this.handleChangeCoopSelected}
                        />
                    </div>
                    <div className="filter-l">
                        <p>分配类型</p>
                        <Select
                            options={distributeStateList}
                            value={distributeStateSelected}
                            onChange={this.handleChangeDistributeStateSelected}
                        />
                    </div>
                </div>
                <div className="client-top">
                    <div className="filter-l" style={{ flex: 0.6 }}>
                        <p>门店id</p>
                        <Input
                            className="wmpoi"
                            type="text"
                            status={this.state.wmPoiIdState}
                            value={queryWmPoiId}
                            suffix={<Icon name="times-circle" onClick={this.handleClearWmPoiId} />}
                            onChange={this.handleWmPoiIdChange}
                        />
                    </div>
                    <div className="filter-l">
                        <p>分配时间</p>
                        <RangePicker
                            clearable
                            // @ts-ignore
                            // `value` 的类型声明为 `Dayjs` 和 `string`，实际上支持 `number`
                            // `valueOfType` 为 `timestamp` 或 `second` 时，`onChange` 获得的类型是 `number`
                            value={distributeTime}
                            valueOfType="second"
                            onChange={this.handleDistributeTimeChange}
                            onCalendarChange={this.onCalendarChange}
                            onOpenChange={this.onRangePickerOpenChange}
                            disabledDate={this.disabled31DaysDate}
                        />
                    </div>
                    <div className="filter-l">
                        <p style={{ width: '100px' }}>服务开始时间</p>
                        <RangePicker
                            clearable
                            // @ts-ignore
                            value={startTime}
                            valueOfType="second"
                            onChange={this.handleStartTimeChange}
                            onCalendarChange={this.onCalendarChange}
                            onOpenChange={this.onRangePickerOpenChange}
                            disabledDate={this.disabled31DaysDate}
                        />
                    </div>
                </div>
                <div className="client-top">
                    <div className="filter-l">
                        <p>签约时间</p>
                        <RangePicker
                            clearable
                            // @ts-ignore
                            value={poiSignTime}
                            valueOfType="second"
                            onChange={this.handlePoiSignTimeChange}
                            onCalendarChange={this.onCalendarChange}
                            onOpenChange={this.onRangePickerOpenChange}
                            disabledDate={this.disabled31DaysDate}
                        />
                    </div>
                    <div className="filter-l">
                        <div className="query-btn">
                            <Button loading={loading} onClick={this.handleQuery}>
                                查询
                            </Button>
                        </div>

                        <div className="export-btn">
                            <Button loading={exportLoading} onClick={this.handleExport}>
                                导出
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="button-line-second">
                    <Button
                        loading={loading}
                        onClick={this.signByBatch}
                        disabled // 禁用该功能
                    >
                        签约选中项
                    </Button>
                    <Button
                        className="withdraw-all"
                        loading={loading}
                        disabled // 禁用该功能
                        onClick={this.withdrawByBatch}
                    >
                        撤回签约选中项
                    </Button>
                </div>
                <p>
                    符合检索条件的数量：
                    {this.state.total}
                </p>
                <SelectGroup
                    handle={this.selectorHandle}
                    defaultSelected={this.state.defaultSelected}
                    allSelected={this.allSelected}
                >
                    <TableNew
                        rowKey="id"
                        columns={columns}
                        data={distributeList}
                        scroll={{ x: 2000 }}
                        hover
                    />
                </SelectGroup>
                <Pagination total={total} pageSize={PAGE_SIZE} onChange={this.handlePageChange} />
                {
                    /**
                     * 签约表单弹层
                     */
                    showModal === 1 && (
                        <SignModal
                            visible
                            coopMode={
                                // 区分批量签约和单个签约的合作模式
                                this.isBatchOperation ? batchCoopMode : activeItem.coopMode
                            }
                            contractName={activeItem.contractName}
                            onCancel={closeModals}
                            onConfirm={handleSignFormOk}
                            providerId={this.providerId}
                            id={this.isBatchOperation ? +batchSignDemoItem.id : +activeItem.id}
                        />
                    )
                }
                {
                    /**
                     * 查看合同弹层
                     */
                    showModal === 2 && (
                        <ContractModal visible data={activeItem} onCancel={closeModals} />
                    )
                }
                {
                    /**
                     * 撤回签约弹层
                     */
                    showModal === 3 && (
                        <Modal
                            title="商家可能正在签约，确定要撤回吗？"
                            visible
                            status="info"
                            onCancel={() => closeModals(true)}
                            onConfirm={this.handleWithdraw}
                        />
                    )
                }
                {showModal === 4 && (
                    <Modal
                        visible
                        onCancel={() => closeModals(true)}
                        onConfirm={this.handleAddReason}
                    >
                        <div className="reason-modal-ct">
                            <p>未签约原因</p>
                            <Input.Textarea
                                placeholder="请补充与商家未达成合作的原因"
                                value={this.state.reason}
                                onChange={this.handleReasonChange}
                                statistics
                                maxLength={100}
                                resize="none"
                                rows={5}
                                cols={40}
                            />
                        </div>
                    </Modal>
                )}
                {showModal === 5 && (
                    <Modal
                        visible
                        onCancel={() => closeModals(true)}
                        showConfirmButton={false}
                        cancelText="我知道了"
                    >
                        <p>{`负责人电话：${privateNumber}`}</p>
                        <br />
                        <p>
                            拨打方式：先拨打前11位手机号，待听到语音提示【请输入分机号】后在10秒内输入4位分机号即可。
                        </p>
                    </Modal>
                )}
                {showModal === 6 && (
                    <Modal
                        visible
                        onCancel={() => closeModals(true)}
                        showConfirmButton={false}
                        cancelText="我知道了"
                    >
                        <p>{`业务经理：${managerMisId}`}</p>
                        <br />
                        <p>联系方法：通过美团大象搜索找到业务经理，与其联系。</p>
                    </Modal>
                )}
                {showModal === 7 && (
                    <SignBatchProgressModal
                        signData={this.state.signData}
                        providerId={this.providerId}
                        getAllSelect={this.getAllSelect}
                        columns={this.modalColumns}
                        batchOperation={this.state.batchOperation}
                        distributeList={this.state.distributeList}
                        onCancel={this.closeModals}
                    />
                )}
                {showModal === 8 && (
                    <Modal
                        title="操作记录"
                        visible
                        onCancel={() => this.closeModals(true)}
                        onConfirm={() => this.closeModals(true)}
                        size="lg" // 调整弹窗大小为更宽的尺寸
                        style={{ width: '80vw', maxWidth: '1200px' }} // 设置弹窗宽度
                        // bodyStyle={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }} // 限制弹窗内容高度并启用滚动
                    >
                        <DistributeRecord distributeId={activeItem.id} providerId={this.providerId} />
                    </Modal>
                )}
            </div>
        );
    }
}

export default List;

const ParagraphOpenHide = (props: any) => {
    const { content = [] } = props;
    const [visible, setVisible] = useState(false);
    const newArr = content.map(item => {
        let res;
        try {
            res = JSON.parse(item);
        } catch (e) {
            res = item;
        }
        return res;
    });
    // 计算高度，元素里面存在数组的情况double
    const rowNum = newArr.reduce(
        (total: number, item) => total + (Array.isArray(item) ? item.length * 2 : 1),
        0,
    );

    return (
        <div style={{ position: 'relative', marginBottom: '20px' }}>
            <div
                style={{
                    height: visible ? '100%' : '100px',
                    overflow: visible ? 'visible' : 'hidden',
                }}
            >
                {content.map(item => (
                    <div>{item}</div>
                ))}
            </div>
            {rowNum > 5 ? (
                <div style={{ position: 'absolute', bottom: '-30px', right: 0 }}>
                    <Button type="text" onClick={() => setVisible(!visible)}>
                        {visible ? '🔼收起' : '···🔽展开'}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
